/* ============================================================
   AI Hero Chat — gofor360
   Frontend: handles chat UI + calls backend endpoint
   ============================================================ */

(function () {
  'use strict';

  const SECTIONS = document.querySelectorAll('.ai-hero[data-endpoint]');
  if (!SECTIONS.length) return;

  SECTIONS.forEach(initSection);

  function initSection(section) {
    const endpoint = section.dataset.endpoint;
    const chatPageUrl = (section.dataset.chatPage || '').trim();
    const form = section.querySelector('[data-ai-chat-form]');
    const input = section.querySelector('[data-ai-chat-input]');
    const conversation = section.querySelector('[data-ai-chat-conversation]');
    const messagesEl = section.querySelector('[data-ai-chat-messages]');
    const closeBtn = section.querySelector('[data-ai-chat-close]');
    const chips = section.querySelectorAll('.ai-hero__chip');
    const scrollHint = section.querySelector('[data-ai-scroll-hint]');

    // ---- i18n: on non-primary locales, swap the Italian theme-editor copy ----
    // On /it we leave whatever Federico typed in the theme editor untouched.
    const i18n = window.AII18n;
    if (i18n && !i18n.isPrimary) {
      const setText = (sel, key) => {
        const el = section.querySelector(sel);
        if (el) el.textContent = i18n.t(key);
      };
      setText('.ai-hero__eyebrow',    'heroEyebrow');
      setText('.ai-hero__heading',    'heroHeading');
      setText('.ai-hero__subheading', 'heroSubheading');
      if (input) input.placeholder = i18n.t('inputPlaceholder');

      const hintLabel = section.querySelector('.ai-hero__scroll-hint-label');
      if (hintLabel) hintLabel.textContent = i18n.t('scrollHint');

      // Chips: translate label + the prompt that gets sent
      chips.forEach((chip, i) => {
        const n = i + 1;
        if (n > 4) return; // only the 4 default chips have translations
        const label  = i18n.t('chip' + n);
        const prompt = i18n.t('chip' + n + 'Prompt');
        if (label)  chip.textContent = label;
        if (prompt) chip.dataset.prompt = prompt;
      });
    }

    // ---- user-interaction tracking (kills auto behaviors once user engages) ----
    let userEngaged = false;
    function markEngaged() {
      userEngaged = true;
      cancelAutoScroll();
    }
    section.addEventListener('mousedown', markEngaged, { once: true });
    section.addEventListener('keydown', markEngaged, { once: true });
    section.addEventListener('touchstart', markEngaged, { once: true, passive: true });
    if (input) input.addEventListener('focus', markEngaged, { once: true });

    // ---- scroll-down hint ----
    if (scrollHint) {
      const delaySec = parseFloat(section.dataset.scrollHintDelay || '4');
      const showTimer = setTimeout(() => {
        if (window.scrollY < section.offsetHeight * 0.5) {
          scrollHint.classList.add('is-visible');
        }
      }, delaySec * 1000);

      const onScroll = () => {
        if (window.scrollY > 40) {
          scrollHint.classList.remove('is-visible');
          window.removeEventListener('scroll', onScroll);
          clearTimeout(showTimer);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });

      scrollHint.addEventListener('click', () => {
        markEngaged();
        const target = section.offsetTop + section.offsetHeight - 20;
        window.scrollTo({ top: target, behavior: 'smooth' });
      });

      if (input) {
        input.addEventListener('focus', () => scrollHint.classList.remove('is-visible'), { once: true });
      }
    }

    // ---- auto-scroll after delay (gentle nudge) ----
    let autoScrollTimer = null;
    function cancelAutoScroll() {
      if (autoScrollTimer) {
        clearTimeout(autoScrollTimer);
        autoScrollTimer = null;
      }
    }
    if (section.dataset.autoScroll === 'true') {
      const autoDelay = parseFloat(section.dataset.autoScrollDelay || '8') * 1000;
      autoScrollTimer = setTimeout(() => {
        if (userEngaged) return;
        if (window.scrollY > 40) return; // user already scrolled
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const target = section.offsetTop + section.offsetHeight - 20;
        window.scrollTo({ top: target, behavior: 'smooth' });
      }, autoDelay);
      // Cancel on any scroll
      window.addEventListener('scroll', () => {
        if (window.scrollY > 10) cancelAutoScroll();
      }, { passive: true, once: true });
    }

    // Local conversation state — sent on every request so backend stays stateless
    const history = [];

    // ---- helper: handle redirect to chat page (or fallback inline) ----
    function handleFirstMessage(text) {
      markEngaged();
      if (chatPageUrl) {
        // Persist the message so the chat page can pick it up immediately
        try {
          sessionStorage.setItem('aiHeroFirstMessage', text);
        } catch (e) { /* private mode */ }
        // Keep the visitor inside their language (/en/pages/..., /de/pages/...)
        const target = window.AII18n ? window.AII18n.url(chatPageUrl) : chatPageUrl;
        const sep = target.includes('?') ? '&' : '?';
        window.location.href = target + sep + 'q=' + encodeURIComponent(text);
        return true;
      }
      return false;
    }

    // ---- product autocomplete on the hero input ----
    if (window.AIProductSearch && input) {
      window.AIProductSearch.attach(input, {
        anchor: form,
        onSelect: () => true // let the browser follow the product link
      });
    }

    // ---- chip click → send prompt (redirect or inline) ----
    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const prompt = chip.dataset.prompt && chip.dataset.prompt.trim()
          ? chip.dataset.prompt.trim()
          : chip.textContent.trim();
        if (handleFirstMessage(prompt)) return;
        sendMessage(prompt);
      });
    });

    // ---- form submit ----
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const text = input.value.trim();
      if (!text) return;
      input.value = '';
      if (handleFirstMessage(text)) return;
      sendMessage(text);
    });

    // ---- close conversation ----
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        conversation.hidden = true;
        history.length = 0;
        messagesEl.innerHTML = '';
      });
    }

    // ---- main send ----
    async function sendMessage(text) {
      // open conversation drawer
      conversation.hidden = false;

      appendMessage('user', text);
      history.push({ role: 'user', content: text });

      const typingEl = appendTyping();

      try {
        if (!endpoint) {
          // No endpoint configured — friendly demo fallback
          await wait(700);
          typingEl.remove();
          const demo = demoReply(text);
          appendMessage('ai', demo);
          history.push({ role: 'assistant', content: demo });
          return;
        }

        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: history,
            shop: section.dataset.shopDomain || window.Shopify?.shop || '',
            locale: (window.AII18n && window.AII18n.locale) || document.documentElement.lang || 'it'
          })
        });

        if (!res.ok) {
          throw new Error('HTTP ' + res.status);
        }

        const data = await res.json();
        typingEl.remove();

        // Backend response shape:
        //   { reply: "text", products: [{ title, url, image, price }] }
        const reply = (data.reply || '').trim();
        appendMessage('ai', reply, data.products || []);
        history.push({ role: 'assistant', content: reply });
      } catch (err) {
        console.error('[ai-hero] chat error', err);
        typingEl.remove();
        appendMessage('ai', "Mi dispiace, c'è stato un problema. Riprova tra poco oppure scrivici su WhatsApp.");
      }
    }

    // ---- helpers ----
    function appendMessage(role, text, products) {
      const el = document.createElement('div');
      el.className = 'ai-hero__msg ai-hero__msg--' + (role === 'user' ? 'user' : 'ai');
      el.textContent = text;

      if (products && Array.isArray(products) && products.length > 0) {
        products.forEach((p) => {
          const card = document.createElement('a');
          card.className = 'ai-hero__product-card';
          card.href = p.url || '#';
          if (p.image) {
            const img = document.createElement('img');
            img.src = p.image;
            img.alt = p.title || '';
            card.appendChild(img);
          }
          const meta = document.createElement('div');
          const t = document.createElement('div'); t.textContent = p.title || ''; t.style.fontWeight = '600';
          const pr = document.createElement('div'); pr.textContent = p.price || ''; pr.style.opacity = '.85';
          meta.appendChild(t); meta.appendChild(pr);
          card.appendChild(meta);
          el.appendChild(card);
        });
      }

      messagesEl.appendChild(el);
      messagesEl.scrollTop = messagesEl.scrollHeight;
      return el;
    }

    function appendTyping() {
      const el = document.createElement('div');
      el.className = 'ai-hero__msg ai-hero__msg--ai';
      el.innerHTML = '<span class="ai-hero__typing"><span></span><span></span><span></span></span>';
      messagesEl.appendChild(el);
      messagesEl.scrollTop = messagesEl.scrollHeight;
      return el;
    }

    function wait(ms) { return new Promise((r) => setTimeout(r, ms)); }

    function demoReply(text) {
      const t = text.toLowerCase();
      if (t.includes('dorm') || t.includes('sonno') || t.includes('insonn')) {
        return 'Per il sonno consiglio di partire dal nostro MAG5+ (5 forme di magnesio) o dal Melatonin Pure. Dimmi se hai difficoltà a addormentarti o ti svegli di notte e ti propongo il più adatto.\n\n[Demo: collega il tuo backend AI per risposte personalizzate]';
      }
      if (t.includes('energ') || t.includes('stanc')) {
        return 'Capisco, la stanchezza cronica è uno dei motivi più frequenti per cui si parte con un integratore. Spesso il problema sono ferro/ vit B/ carenze di magnesio. Ti consiglio Metabolic Energy + un controllo della ferritina.\n\n[Demo: collega il tuo backend AI per risposte personalizzate]';
      }
      if (t.includes('magn')) {
        return 'I nostri integratori a base di magnesio differiscono per forma chimica e biodisponibilità. MAG5+ unisce 5 forme (citrato, glicinato, taurato, malato, marino) per un\'azione completa.\n\n[Demo: collega il tuo backend AI per risposte personalizzate]';
      }
      return "Grazie per la tua domanda! Per ricevere consigli personalizzati basati sui nostri 118 prodotti, configura il backend AI nel tema (vedi documentazione). Per ora questa è una risposta demo.\n\nIntanto puoi esplorare il nostro shop o scriverci su WhatsApp.";
    }
  }
})();
